<template>
  <div class="custom-input">
    <label class="input-label" v-if="$validate.DataValid(label)">{{ $t(label) }}</label>
    <v-menu
      ref="pickerMenu"
      v-model="pickerOpen"
      transition="scale-transition"
      offset-y
      min-width="auto"
      :close-on-content-click="false"
      :disabled="disabled || readonly"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          persistent-placeholder
          :value="fieldValue"
          v-bind="attrs"
          v-on="on"
          readonly
          outlined
          single-line
          :rules="rules"
          :hide-details="hideDetails"
          :placeholder="$t(placeholder)"
          :dense="dense"
          :class="inputClass"
          @click:clear="onClear()"
          :clearable="enableClear"
          :hint="$t(hint)"
          :persistent-hint="hint !== ''"
          :disabled="disabled"
        ></v-text-field>
      </template>

      <div class="d-flex time-scroller-container">
        <v-list class="time-scroller">
          <span>HH</span>
          <v-list-item-group v-model="selectedHour" @change="updateValue()">
            <v-list-item
              link
              v-for="value in hourOptions"
              :value="value"
              :key="`hour_${value}`"
              :disabled="disableSelect(value, 'h')"
              >{{ value }}</v-list-item
            >
          </v-list-item-group>
        </v-list>
        <v-list class="time-scroller">
          <span>MM</span>
          <v-list-item-group v-model="selectedMinute" @change="updateValue()">
            <v-list-item
              link
              v-for="value in minuteOptions"
              :value="value"
              :key="`min_${value}`"
              :disabled="disableSelect(value, 'm')"
              >{{ value }}</v-list-item
            >
          </v-list-item-group>
        </v-list>
      </div>
    </v-menu>
  </div>
</template>

<script>
export default {
  name: 'FormTimePicker',
  props: {
    label: {
      type: String,
      required: false,
      default: '',
    },
    fieldValue: {
      type: String,
      required: true,
      default: '',
    },
    options: {
      type: Array,
      required: true,
      default: () => [],
    },
    hideDetails: {
      type: Boolean,
      required: false,
      default: false,
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    customRules: {
      type: Array,
      required: false,
      default: () => [],
    },
    dense: {
      type: Boolean,
      required: false,
      default: true,
    },
    customInputClass: {
      type: String,
      required: false,
      default: '',
    },
    dispatchUpdateOnChange: {
      type: Boolean,
      required: false,
      default: false,
    },
    enableClear: {
      type: Boolean,
      required: false,
      default: false,
    },
    hint: {
      type: String,
      required: false,
      default: '',
    },
  },
  computed: {
    rules() {
      return [
        v => (this.required ? this.$validate.required(v) || this.$t('message.formRequired') : true),
        v => this.gfCheckRule(this.$validate.isValidTime(v, !this.required)),
        ...this.customRules,
      ]
    },
    inputClass() {
      let classObj = {
        'pointer-none': this.$store.getters.isLoading,
      }
      if (this.$validate.DataValid(this.customInputClass)) {
        const arr = this.customInputClass.split(' ')
        arr.forEach(name => {
          if (this.$validate.DataValid(name.trim())) {
            classObj[name.trim()] = true
          }
        })
      }

      return classObj
    },
    hourOptions() {
      const list = []
      if (this.options.length) {
        for (let i = 0; i < this.options.length; i++) {
          const h = this.options[i].split(':')[0]
          if (h && !list.includes(h)) {
            list.push(h)
          }
        }
      }

      list.sort((a, b) => parseInt(a) - parseInt(b))
      return list
    },
    minuteOptions() {
      const list = []
      if (this.options.length) {
        for (let i = 0; i < this.options.length; i++) {
          const h = this.options[i].split(':')[1]
          if (h && !list.includes(h)) {
            list.push(h)
          }
        }
      }

      list.sort((a, b) => parseInt(a) - parseInt(b))
      return list
    },
  },
  watch: {
    fieldValue: {
      handler(val) {
        if (!val) {
          this.selectedHour = ''
          this.selectedMinute = ''
        } else {
          const parts = val.split(':')
          if (parts.length) {
            if (this.$validate.isNumber(parts[0]) === true) {
              this.selectedHour = parts[0]
            }

            if (parts.length > 1 && this.$validate.isNumber(parts[1]) === true) {
              this.selectedMinute = parts[1]
            }
          }
        }
      },
      immediate: true,
      deep: true,
    },
  },
  data: () => ({
    pickerOpen: false,
    selectedHour: '',
    selectedMinute: '',
  }),
  methods: {
    updateValue() {
      let value = ''
      if (this.selectedHour || this.selectedMinute) {
        if (this.selectedHour) {
          value = this.selectedHour
        } else {
          value = '-'
        }

        value += ':'

        if (this.selectedMinute) {
          value += this.selectedMinute
        } else {
          value += '-'
        }
      }
      this.$emit('update:fieldValue', value)
      this.$emit('changed', value)
      if (this.dispatchUpdateOnChange === true) {
        this.$store.dispatch('setDataIsUpdated', true)
      }
    },
    onClear() {
      this.selectedHour = ''
      this.selectedMinute = ''
      this.updateValue()
    },
    disableSelect(value, type) {
      let disabled = false
      let text = ''
      if (type === 'h' && this.selectedMinute) {
        text = `${value}:${this.selectedMinute}`
      } else if (type === 'm' && this.selectedHour) {
        text = `${this.selectedHour}:${value}`
      }
      if (text && !this.options.includes(text)) {
        return true
      }
      return disabled
    },
  },
}
</script>
<style lang="scss" scoped>
.time-scroller-container {
  width: 100%;
  background-color: #ffffff;

  & .time-scroller {
    overflow-x: hidden;
    overflow-y: auto;
    max-height: 300px;

    & .v-list-item {
      cursor: pointer;
      font-size: 0.875rem;
      font-weight: 400;
      letter-spacing: 0.08em;
      background-color: #ffffff;
    }

    & > span {
      width: 100%;
      text-align: center;
      color: var(--v-colorGrey-base);
      display: block;
      font-size: 12px;
    }
  }
}

.v-item--active {
  color: var(--v-primary-base);
  font-weight: 600;
}
</style>
